// InfoCard component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.2

import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { tokens } from '../../../styles/tokens'
import { InfoCardProps, InfoRowProps } from './info-card.types'

// Styled card container
const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: tokens.radius.lg,
  overflow: 'hidden',
}))

// Styled header
const CardHeaderStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '14px 20px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

// Styled row container
const RowContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<{ isLast?: boolean }>(({ theme, isLast }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  borderBottom: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
}))

// Info row component
const InfoRowComponent: React.FC<InfoRowProps> = ({
  label,
  value,
  isLink,
  isSuccess,
  isError,
  isLast,
}) => {
  const theme = useTheme()

  const getValueColor = () => {
    if (isSuccess) return theme.palette.success.main
    if (isError) return theme.palette.error.main
    if (isLink) return theme.palette.primary.main
    return theme.palette.text.primary
  }

  return (
    <RowContainer isLast={isLast}>
      <Typography
        sx={{
          fontSize: 13,
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography
          component={isLink ? 'a' : 'span'}
          href={isLink ? `mailto:${value}` : undefined}
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: getValueColor(),
            textDecoration: isLink ? 'none' : 'inherit',
            cursor: isLink ? 'pointer' : 'inherit',
            '&:hover': isLink
              ? {
                  textDecoration: 'underline',
                }
              : {},
          }}
        >
          {value}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>{value}</Box>
      )}
    </RowContainer>
  )
}

// Main InfoCard component
export const InfoCard: React.FC<InfoCardProps> = ({
  title,
  icon,
  rows,
  sx,
  className,
}) => {
  return (
    <CardContainer sx={sx} className={className}>
      <CardHeaderStyled>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
              fontSize: 14,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'text.disabled',
          }}
        >
          {title}
        </Typography>
      </CardHeaderStyled>
      <Box>
        {rows.map((row, index) => (
          <InfoRowComponent
            key={row.label}
            {...row}
            isLast={index === rows.length - 1}
          />
        ))}
      </Box>
    </CardContainer>
  )
}

InfoCard.displayName = 'InfoCard'

export default InfoCard
export type { InfoCardProps, InfoRow, InfoRowProps } from './info-card.types'
